/* ==========================================================
   Custom Video Player — Controller
   Save as: assets/custom-video-player.js

   Handles:
   - Native Shopify-hosted MP4 <video> elements
   - YouTube embeds via the YouTube IFrame API
   - Vimeo embeds via the Vimeo Player SDK
   - A single custom play/pause button per instance
   - Multiple player instances on the same page

   Behavior:
   - Sound: videos honor the "Start muted" setting for manual play.
     Autoplay is still forced-muted (browsers block audible
     autoplay), but the moment a user clicks play/pause or the
     video itself, that's a real gesture — sound is restored
     according to the configured setting.
   - Exclusivity: only one instance plays at a time. Starting any
     player (native, YouTube, or Vimeo) pauses whichever other
     instance was previously playing, regardless of provider.
   - Visibility: a player pauses itself once its root element is
     no longer intersecting the viewport — covers normal scrolling
     away as well as sliding out of view inside a carousel track
     (IntersectionObserver accounts for ancestor overflow clipping).
   - Click-to-toggle: clicking anywhere on the video (not just the
     button) toggles play/pause. Native <video> elements get a
     direct click listener; YouTube/Vimeo iframes are cross-origin
     and swallow clicks, so those get a transparent overlay layer
     sitting on top of the iframe (beneath the visible button) that
     forwards clicks back into our toggle logic.
   ========================================================== */

(function () {
  "use strict";

  var youtubeApiPromise = null;
  var vimeoApiPromise = null;

  /* ---------------------------------------------------------
     Fix #2 — only one video plays at a time.
     Every instance registers a { pause } controller here keyed
     by blockId. Whenever an instance starts playing, every other
     registered instance gets told to pause.
  --------------------------------------------------------- */
  var activeBlockId = null;
  var controllers = {};

  function registerController(blockId, controller) {
    controllers[blockId] = controller;
  }

  function notifyPlaying(blockId) {
    if (activeBlockId && activeBlockId !== blockId) {
      var prev = controllers[activeBlockId];
      if (prev && typeof prev.pause === "function") {
        prev.pause();
      }
    }
    activeBlockId = blockId;
  }

  function notifyPaused(blockId) {
    if (activeBlockId === blockId) {
      activeBlockId = null;
    }
  }

  /* ---------------------------------------------------------
     Fix #3 — pause automatically once the player's root element
     is no longer visible: normal page scroll-away, or sliding out
     of view inside an overflow:hidden carousel track.
  --------------------------------------------------------- */
  function bindVisibilityPause(root, pauseFn) {
    if (!("IntersectionObserver" in window)) return;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            pauseFn();
          }
        });
      },
      { threshold: 0 }
    );
    observer.observe(root);
  }

  /* ---------------------------------------------------------
     Fix #4 — let clicking the video itself toggle play/pause.
     For cross-origin iframes (YouTube/Vimeo) a native click never
     reaches our page once the pointer enters the iframe, so we lay
     a transparent catcher over the media and forward its clicks.
     The visible play/pause button is kept above this layer
     (z-index) so it keeps working exactly as before.
  --------------------------------------------------------- */
  function addClickToggleLayer(mediaEl, btn, toggleFn) {
    if (!mediaEl || mediaEl.querySelector(".cvp__click-catch")) return;

    var computedPosition = window.getComputedStyle(mediaEl).position;
    if (computedPosition === "static" || !computedPosition) {
      mediaEl.style.position = "relative";
    }

    var layer = document.createElement("div");
    layer.className = "cvp__click-catch";
    layer.setAttribute("aria-hidden", "true");
    layer.style.position = "absolute";
    layer.style.inset = "0";
    layer.style.zIndex = "1";
    layer.style.cursor = "pointer";
    layer.style.background = "transparent";
    mediaEl.appendChild(layer);

    if (btn) {
      var btnPosition = window.getComputedStyle(btn).position;
      if (btnPosition === "static") btn.style.position = "relative";
      btn.style.zIndex = "2";
    }

    layer.addEventListener("click", function (e) {
      e.stopPropagation();
      toggleFn();
    });
  }

  function loadYouTubeApi() {
    if (youtubeApiPromise) return youtubeApiPromise;
    youtubeApiPromise = new Promise(function (resolve) {
      if (window.YT && window.YT.Player) {
        resolve(window.YT);
        return;
      }
      var existing = document.getElementById("cvp-youtube-api");
      if (!existing) {
        var tag = document.createElement("script");
        tag.id = "cvp-youtube-api";
        tag.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(tag);
      }
      var prevCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof prevCallback === "function") prevCallback();
        resolve(window.YT);
      };
    });
    return youtubeApiPromise;
  }

  function loadVimeoApi() {
    if (vimeoApiPromise) return vimeoApiPromise;
    vimeoApiPromise = new Promise(function (resolve) {
      if (window.Vimeo && window.Vimeo.Player) {
        resolve(window.Vimeo);
        return;
      }
      var existing = document.getElementById("cvp-vimeo-api");
      if (existing) {
        existing.addEventListener("load", function () {
          resolve(window.Vimeo);
        });
        return;
      }
      var tag = document.createElement("script");
      tag.id = "cvp-vimeo-api";
      tag.src = "https://player.vimeo.com/api/player.js";
      tag.onload = function () {
        resolve(window.Vimeo);
      };
      document.head.appendChild(tag);
    });
    return vimeoApiPromise;
  }

  function initNativePlayer(root, config) {
    var video = root.querySelector(".cvp__video");
    var btn = root.querySelector(".cvp__btn");
    var mediaEl = root.querySelector(".cvp__media");
    if (!video || !btn) return;

    // Fix #1 — respect the "Start muted" setting instead of leaving
    // the video's mute state undefined. Autoplay still forces mute
    // (required for browsers to allow it to start automatically).
    video.muted = config.autoplay ? true : !!config.muted;

    function play() {
      if (video.paused) {
        video.play().catch(function () {});
      }
    }

    function pause() {
      if (!video.paused) {
        video.pause();
      }
    }

    function togglePlay() {
      if (video.paused) {
        // Real user gesture — restore the configured sound setting
        // rather than staying forced-muted from autoplay.
        video.muted = !!config.muted;
        play();
      } else {
        pause();
      }
    }

    registerController(config.blockId, { pause: pause });

    if (config.autoplay) {
      video.play().then(function () {
        root.setAttribute("data-playing", "true");
        notifyPlaying(config.blockId);
      }).catch(function () {
        /* autoplay blocked by browser — leave paused, user can hit play */
      });
    }

    btn.addEventListener("click", function (e) {
      e.stopPropagation();
      togglePlay();
    });

    // Fix #4 — clicking the video itself also toggles playback.
    video.addEventListener("click", togglePlay);

    video.addEventListener("play", function () {
      root.setAttribute("data-playing", "true");
      btn.setAttribute("aria-label", "Pause video");
      notifyPlaying(config.blockId);
    });
    video.addEventListener("pause", function () {
      root.setAttribute("data-playing", "false");
      btn.setAttribute("aria-label", "Play video");
      notifyPaused(config.blockId);
    });
    video.addEventListener("ended", function () {
      if (!config.loop) {
        root.setAttribute("data-playing", "false");
        btn.setAttribute("aria-label", "Play video");
        notifyPaused(config.blockId);
      }
    });

    // Fix #3 — pause when scrolled/slid out of view.
    bindVisibilityPause(root, pause);
  }

  function initYouTubePlayer(root, config, mediaEl) {
    var frame = mediaEl.querySelector(".cvp__external-frame");
    var btn = root.querySelector(".cvp__btn");
    var videoId = mediaEl.getAttribute("data-video-id");
    var player = null;
    var playerReady = false;

    function play() {
      if (player && playerReady) player.playVideo();
    }

    function pause() {
      if (player && playerReady) player.pauseVideo();
    }

    function togglePlay() {
      if (!player || !playerReady) return;
      var state = player.getPlayerState();
      if (state === window.YT.PlayerState.PLAYING) {
        pause();
      } else {
        // Real user gesture — restore configured sound.
        if (!config.muted) player.unMute();
        play();
      }
    }

    registerController(config.blockId, { pause: pause });

    loadYouTubeApi().then(function (YT) {
      player = new YT.Player(frame, {
        videoId: videoId,
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          loop: config.loop ? 1 : 0,
          mute: config.muted ? 1 : 0,
          playlist: config.loop ? videoId : undefined
        },
        events: {
          onReady: function () {
            playerReady = true;
            // Fix #1 — apply the configured mute state explicitly.
            if (config.muted) {
              player.mute();
            } else {
              player.unMute();
            }
            if (config.autoplay) {
              player.mute();
              player.playVideo();
            }
          },
          onStateChange: function (e) {
            if (e.data === YT.PlayerState.PLAYING) {
              root.setAttribute("data-playing", "true");
              btn.setAttribute("aria-label", "Pause video");
              notifyPlaying(config.blockId);
            } else if (
              e.data === YT.PlayerState.PAUSED ||
              e.data === YT.PlayerState.ENDED
            ) {
              root.setAttribute("data-playing", "false");
              btn.setAttribute("aria-label", "Play video");
              notifyPaused(config.blockId);
            }
          }
        }
      });
    });

    btn.addEventListener("click", function (e) {
      e.stopPropagation();
      togglePlay();
    });

    // Fix #4 — click-through overlay since the iframe is cross-origin.
    addClickToggleLayer(mediaEl, btn, togglePlay);

    // Fix #3 — pause when scrolled/slid out of view.
    bindVisibilityPause(root, pause);
  }

  function initVimeoPlayer(root, config, mediaEl) {
    var frame = mediaEl.querySelector(".cvp__external-frame");
    var btn = root.querySelector(".cvp__btn");
    var videoId = mediaEl.getAttribute("data-video-id");
    var player = null;

    function play() {
      if (!player) return;
      player.play().catch(function () {});
    }

    function pause() {
      if (!player) return;
      player.pause().catch(function () {});
    }

    function togglePlay() {
      if (!player) return;
      player.getPaused().then(function (paused) {
        if (paused) {
          // Real user gesture — restore configured sound.
          if (!config.muted) player.setVolume(1);
          play();
        } else {
          pause();
        }
      });
    }

    registerController(config.blockId, { pause: pause });

    loadVimeoApi().then(function (Vimeo) {
      player = new Vimeo.Player(frame, {
        id: videoId,
        controls: false,
        autoplay: false,
        loop: !!config.loop,
        muted: !!config.muted,
        playsinline: true,
        responsive: true
      });

      if (config.autoplay) {
        player.setVolume(0);
        player.play().catch(function () {
          /* autoplay blocked — user can hit the custom button */
        });
      }

      player.on("play", function () {
        root.setAttribute("data-playing", "true");
        btn.setAttribute("aria-label", "Pause video");
        notifyPlaying(config.blockId);
      });
      player.on("pause", function () {
        root.setAttribute("data-playing", "false");
        btn.setAttribute("aria-label", "Play video");
        notifyPaused(config.blockId);
      });
      player.on("ended", function () {
        if (!config.loop) {
          root.setAttribute("data-playing", "false");
          btn.setAttribute("aria-label", "Play video");
          notifyPaused(config.blockId);
        }
      });

      btn.addEventListener("click", function (e) {
        e.stopPropagation();
        togglePlay();
      });

      // Fix #4 — click-through overlay since the iframe is cross-origin.
      addClickToggleLayer(mediaEl, btn, togglePlay);

      // Fix #3 — pause when scrolled/slid out of view.
      bindVisibilityPause(root, pause);
    });
  }

  function initInstance(config) {
    var root = document.getElementById("cvp-" + config.blockId);
    if (!root) return;

    var mediaEl = root.querySelector(".cvp__media");
    if (!mediaEl) return;

    var type = mediaEl.getAttribute("data-type");

    if (type === "native") {
      initNativePlayer(root, config);
    } else if (type === "external") {
      var provider = mediaEl.getAttribute("data-provider");
      if (provider === "youtube") {
        initYouTubePlayer(root, config, mediaEl);
      } else if (provider === "vimeo") {
        initVimeoPlayer(root, config, mediaEl);
      }
    }
  }

  function initAll() {
    var queue = window.cvpInstances || [];
    queue.forEach(initInstance);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  /* Re-init when a section is added/reloaded in the theme editor.
     A section can contain multiple custom-video-player blocks, so
     re-init every queued instance whose root element now exists
     inside the reloaded section. */
  document.addEventListener("shopify:section:load", function (event) {
    var sectionRoot = event.target;
    (window.cvpInstances || []).forEach(function (config) {
      var el = document.getElementById("cvp-" + config.blockId);
      if (el && sectionRoot.contains(el)) {
        initInstance(config);
      }
    });
  });
})();